import { useRef } from 'react';
import { Chip } from '@material-ui/core';
import { useDrag, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';

/**
 * @param {{
 *  index: number;
 *  costCenter: string;
 *  onMove: (dragIndex: number, hoverIndex: number) => void;
 *  onRemove: (index: number) => void;
 * }} param0
 */
const DraggableCostCenterPill = ({ index, costCenter, onMove, onRemove }) => {
    const { t } = useTranslation('globalSetup', { keyPrefix: 'orgJobPathCostCenterConfig' });
    const ref = useRef(null);

    const [{ isDragging }, drag] = useDrag({
        type: 'COST_CENTER',
        item: () => ({ index, costCenter }),
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        end: (item, monitor) => {
            // Reset if dropped outside valid drop zone
            if (!monitor.didDrop()) {
                // Item was dropped outside - do nothing, keep original order
            }
        },
    });

    const [{ isOver }, drop] = useDrop({
        accept: 'COST_CENTER',
        hover: (item, monitor) => {
            if (!ref.current) {
                return;
            }
            
            // Don't replace items with themselves
            if (item.index === index) {
                return;
            }

            // Determine rectangle on screen
            const hoverBoundingRect = ref.current.getBoundingClientRect();
            
            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            
            // Determine mouse position
            const clientOffset = monitor.getClientOffset();
            
            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            
            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%
            
            const dragIndex = item.index;
            const hoverIndex = index;
            
            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }
            
            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            // Time to actually perform the action
            onMove(dragIndex, hoverIndex);
            
            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex;
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    });

    drag(drop(ref));

    return (
        <div
            ref={ref}
            className="draggable-cost-center-pill"
            style={{
                opacity: isDragging ? 0.5 : 1,
                background: isOver ? '#DDDDDD33' : undefined,
            }}
        >
            <Chip
                icon={<DragIndicatorIcon />}
                label={t('costCenterLabel', { number: costCenter })}
                onDelete={() => onRemove(index)}
                color="primary"
            />
        </div>
    );
};

export default DraggableCostCenterPill;

